import { ColumnDef, Table } from '@tanstack/react-table';
import { BaseCell } from 'src/components/TableV2/cells';
import { Content } from 'src/contexts/ContentContext';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { TableShimmeringDiv } from 'src/tables/Table';
import { SectionType } from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';

const COLUMN_MIN_SIZE = 50;
const COLUMN_MAX_SIZE = 500;

interface TextColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Accessor function to get the text value from data */
  accessor: (data: TData) => string | number | boolean | null | undefined;
  /** Column size */
  size?: number;
  /** Min size */
  minSize?: number;
  /** Max size */
  maxSize?: number;
  /** Text alignment */
  align?: 'left' | 'right' | 'center';
  /** Enable column filter */
  enableColumnFilter?: boolean;
  /** Custom filter function */
  filterFn?: any;
  /** Custom sorting function */
  sortingFn?: any;
  /** Custom cell renderer - can be a function or React component */
  customCellRenderer?:
    | ((props: {
        value: string | number | boolean | null | undefined;
        original: TData;
        columnId: string;
        table: Table<TData>;
        defaultRenderer: () => React.ReactNode;
      }) => React.ReactNode)
    | React.ComponentType<{
        value: string | number | boolean | null | undefined;
        original: TData;
        columnId: string;
        table: Table<TData>;
        defaultRenderer: () => React.ReactNode;
      }>;
  /** Include footer with aggregated totals */
  withFooter?: boolean;
  /** Custom footer renderer */
  customFooterRenderer?: (props: {
    value: string | number | boolean | null | undefined;
    columnId: string;
  }) => React.ReactNode;
  /** Function to determine if cell should render (return false to render null) */
  shouldRender?: (data: TData, table: Table<TData>) => boolean;
}

/**
 * Factory function to create text column definitions for tables.
 * Handles simple text/string display with optional custom rendering.
 *
 * @example
 * createTextColumn({
 *   id: SalesTableColumnId.PaymentReferenceId,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.PaymentReferenceId],
 *   accessor: (data) => data?.sale?.pmtRefId,
 *   size: COLUMN_DEFAULT_SIZE_LG,
 * })
 */
export const createTextColumn = <TData,>(
  options: TextColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    size,
    minSize = COLUMN_MIN_SIZE,
    maxSize = COLUMN_MAX_SIZE,
    align = 'left',
    enableColumnFilter = false,
    filterFn,
    sortingFn,
    customCellRenderer,
    withFooter = false,
    customFooterRenderer,
    shouldRender,
  } = options;

  return {
    id,
    enableColumnFilter,
    ...(filterFn && { filterFn }),
    ...(sortingFn && { sortingFn }),
    size,
    minSize,
    maxSize,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const value = getValue<string | number | boolean | null | undefined>();

      if (!original) {
        return (
          <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
        );
      }

      if (shouldRender && !shouldRender(original, table)) {
        return null;
      }

      if (!value && value !== 0 && value !== false) {
        return <BaseCell />;
      }

      const defaultRenderer = () => (
        <BaseCell align={align}>{String(value)}</BaseCell>
      );

      if (customCellRenderer) {
        // Check if it's a React component class
        const isComponentClass =
          typeof customCellRenderer === 'function' &&
          customCellRenderer.prototype?.isReactComponent;

        if (isComponentClass) {
          const Component = customCellRenderer as React.ComponentType<{
            value: string | number | boolean | null | undefined;
            original: TData;
            columnId: string;
            table: any;
            defaultRenderer: () => React.ReactNode;
          }>;
          return (
            <Component
              value={value}
              original={original}
              columnId={columnId}
              table={table}
              defaultRenderer={defaultRenderer}
            />
          );
        }

        // Otherwise treat as a render function
        return (
          customCellRenderer as (props: {
            value: string | number | boolean | null | undefined;
            original: TData;
            columnId: string;
            table: any;
            defaultRenderer: () => React.ReactNode;
          }) => React.ReactNode
        )({ value, original, columnId, table, defaultRenderer });
      }

      return defaultRenderer();
    },
    ...(withFooter && {
      footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
        const original = table.options.meta?.summaryData;

        if (!original) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const value = accessorFn?.(original, -1) as
          | string
          | number
          | boolean
          | null
          | undefined;

        if (customFooterRenderer) {
          return customFooterRenderer({ value, columnId });
        }

        if (!value && value !== 0 && value !== false) return null;

        return (
          <BaseCell align={align}>
            {value != null && !isNaN(Number(value)) ? value : String(value)}
          </BaseCell>
        );
      },
    }),
  };
};
